import { Color, HeightReference, NearFarScalar, PointGraphics, PolylineOutlineMaterialProperty, } from "cesium";
import { getDefaultLineWorkCrossSectionSettings } from "../settings/earthbrain-site-settings";
import { AssetType, CESIUM_PICKABLE_ENTITY, } from "../types";
import { CrossSectionUtils } from "../utilities/cross-section-utils";
import { EarthBrainUtils } from "../utilities/earthbrainutility";
export class LineWorkCrossSectionHandler {
    _crossSectionManager;
    _lineSectionView;
    //this is the line we will be working on. Clicking the point will change the current line
    _currentLine;
    _site;
    _viewer;
    _handler;
    _cesiumPicker;
    _originalPolylineMaterial;
    _originalSphere;
    _highlightMaterial;
    _sphereHighlight;
    _activeCrossSectionData;
    _onPointClickCallback;
    constructor(onPointClickCallback, args, crossSectionManager, lineSectionView) {
        this._onPointClickCallback = onPointClickCallback;
        this._crossSectionManager = crossSectionManager;
        this._lineSectionView = lineSectionView;
        this._site = args.site;
        this._viewer = args.viewer;
        this._handler = args.handler;
        this._originalPolylineMaterial = undefined;
        this._originalSphere = undefined;
        this._highlightMaterial = new PolylineOutlineMaterialProperty({
            color: Color.RED,
        });
        this._sphereHighlight = new PointGraphics({
            pixelSize: 3.5,
            color: Color.RED,
            heightReference: HeightReference.NONE,
            scaleByDistance: new NearFarScalar(0, 3.5, 2000, 0),
            // Do not set disableDepthTestDistance to avoid making the entity always appear on top
        });
        this._activeCrossSectionData = undefined;
        this._cesiumPicker = args.cesiumPicker;
    }
    onClick(click) {
        // Use drillPick to get all entities at click position
        // Note: drillPick returns any[], similar to getPickablePickedInfo pattern
        const pickedEntities = this._viewer.scene.drillPick(click.position);
        // Find the best entity to interact with using priority rules
        const selectedEntity = this.selectBestEntityFromPick(pickedEntities);
        if (this.hasPickInfo(selectedEntity)) {
            this._currentLine = selectedEntity.pickInfo.owner;
            const entityType = selectedEntity.pickInfo.pickableType;
            let crossSectionData;
            if (entityType === CESIUM_PICKABLE_ENTITY.ARBITRARY_LINE_CONTROL_POINT) {
                crossSectionData = this._crossSectionManager.getCrossSectionForPoint(selectedEntity.id);
            }
            else if (entityType === CESIUM_PICKABLE_ENTITY.LINEWORKS_CROSS_SECTION_LEFT ||
                entityType === CESIUM_PICKABLE_ENTITY.LINEWORKS_CROSS_SECTION_RIGHT) {
                crossSectionData =
                    this._crossSectionManager.getCrossSectionForCrossSectionEntityId(selectedEntity.id, entityType);
            }
            if (crossSectionData) {
                this.renderCrossSectionData(crossSectionData);
                const label = crossSectionData.referencePoint.label;
                this._onPointClickCallback(crossSectionData.referencePoint.lineID, crossSectionData.referencePoint.pointType, crossSectionData.referencePoint.label);
            }
        }
    }
    /**
     * Select the best entity from multiple picked entities based on priority rules.
     * Priority (highest to lowest):
     * 1. Cross-section control points with labels (BP/EP/IP) - labeled points take precedence
     * 2. Cross-section control points without labels
     * 3. Cross-section lines (LEFT/RIGHT)
     * 4. Other arbitrary line entities
     *
     * Note: Similar to getPickablePickedInfo, drillPick returns any[] so we rely on
     * runtime checks. TypeScript allows property access on any without type assertions.
     */
    selectBestEntityFromPick(
    // biome-ignore lint/suspicious/noExplicitAny: drillPick returns any[], same pattern as getPickablePickedInfo
    pickedEntities) {
        if (!pickedEntities || pickedEntities.length === 0) {
            return undefined;
        }
        // Filter only entities with valid pickInfo for arbitrary lines
        // Note: No type assertions needed - same pattern as getPickablePickedInfo
        const arbitraryLineEntities = [];
        for (let i = 0; i < pickedEntities.length; i++) {
            if (pickedEntities[i].id) {
                if (pickedEntities[i].id.allowPicking !== undefined &&
                    pickedEntities[i].id.allowPicking === false) {
                    continue;
                }
                if (pickedEntities[i].id.pickInfo &&
                    pickedEntities[i].id.pickInfo.assetType ===
                        AssetType.ArbitraryLine_Data) {
                    arbitraryLineEntities.push(pickedEntities[i].id);
                }
            }
        }
        if (arbitraryLineEntities.length === 0) {
            return undefined;
        }
        // Priority 1: Cross-section control points
        const controlPointEntities = arbitraryLineEntities.filter((entity) => entity.pickInfo?.pickableType ===
            CESIUM_PICKABLE_ENTITY.ARBITRARY_LINE_CONTROL_POINT);
        if (controlPointEntities.length > 0) {
            // Within control points, prioritize labeled points (BP/EP/IP etc.)
            // Labeled points are important markers and should be clickable even when
            // positioned below other cross-section points
            const labeledControlPoints = controlPointEntities.filter((entity) => {
                // Check if the entity has an associated label in the cross-section data
                const crossSectionData = this._crossSectionManager.getCrossSectionForPoint(entity.id);
                return (crossSectionData?.referencePoint
                    ?.wgs84data?.label !== undefined);
            });
            if (labeledControlPoints.length > 0) {
                return labeledControlPoints[0];
            }
            // If no labeled points, return the first control point
            return controlPointEntities[0];
        }
        // Priority 2: Cross-section lines (left or right)
        const crossSectionEntities = arbitraryLineEntities.filter((entity) => entity.pickInfo?.pickableType ===
            CESIUM_PICKABLE_ENTITY.LINEWORKS_CROSS_SECTION_LEFT ||
            entity.pickInfo?.pickableType ===
                CESIUM_PICKABLE_ENTITY.LINEWORKS_CROSS_SECTION_RIGHT);
        if (crossSectionEntities.length > 0) {
            return crossSectionEntities[0];
        }
        // Priority 3: Other arbitrary line entities
        return arbitraryLineEntities[0];
    }
    /**
     * Type guard to check if entity has pickInfo property
     */
    hasPickInfo(entity) {
        return entity != null && entity.pickInfo !== undefined;
    }
    renderCrossSectionData(crossSectionData) {
        if (this._activeCrossSectionData &&
            this._originalPolylineMaterial &&
            this._originalSphere) {
            this._activeCrossSectionData.leftCrossSectionPolyline.forEach((ele) => {
                ele.polyline.material = this._originalPolylineMaterial;
            });
            this._activeCrossSectionData.rightCrossSectionPolyline.forEach((ele) => {
                ele.polyline.material = this._originalPolylineMaterial;
            });
            if (this._activeCrossSectionData.referencePoint &&
                this._activeCrossSectionData
                    .referencePoint.wgs84data?.sphere?.point) {
                this._activeCrossSectionData
                    .referencePoint.wgs84data.sphere.point = this._originalSphere;
            }
        }
        this._activeCrossSectionData = crossSectionData;
        this._originalPolylineMaterial = crossSectionData
            .leftCrossSectionPolyline[0]
            ? crossSectionData.leftCrossSectionPolyline[0].polyline.material
            : crossSectionData.rightCrossSectionPolyline[0]
                ? crossSectionData.rightCrossSectionPolyline[0].polyline.material
                : new PolylineOutlineMaterialProperty({ color: Color.BLUE });
        this._originalSphere = crossSectionData.referencePoint.wgs84data.sphere?.point;
        crossSectionData.leftCrossSectionPolyline.forEach((ele) => {
            ele.polyline.material = this._highlightMaterial;
        });
        crossSectionData.rightCrossSectionPolyline.forEach((ele) => {
            ele.polyline.material = this._highlightMaterial;
        });
        if (crossSectionData.referencePoint &&
            crossSectionData.referencePoint.wgs84data
                ?.sphere?.point) {
            crossSectionData.referencePoint.wgs84data.sphere.point = this._sphereHighlight;
        }
        this._lineSectionView.renderData(crossSectionData.referencePoint.id, crossSectionData, true);
    }
    /** Render cross sections of the line at the given points. */
    async renderCrossSection(line, crossSectionPoints) {
        // Set current line for pickInfo owner reference
        this._currentLine = line;
        await this.finalizeCrossSectionRendering(line.id, crossSectionPoints);
    }
    deactivateLineWork(lineId) {
        if (this._currentLine && this._currentLine.id === lineId) {
            const crossSections = this._crossSectionManager.getCrossSectionsForLine(this._currentLine.id);
            for (const crossSec of crossSections) {
                crossSec.leftCrossSectionPolyline.forEach((element) => {
                    element.polyline.material = this._originalPolylineMaterial;
                });
                crossSec.rightCrossSectionPolyline.forEach((element) => {
                    element.polyline.material = this._originalPolylineMaterial;
                });
            }
            this._currentLine = undefined;
            this._lineSectionView.clearView();
        }
    }
    async addCrossSection(points, traceablemaps, site, viewer) {
        const crossSections = points.map((point) => CrossSectionUtils.calculateCrossSectionAtEachPoint(point, traceablemaps));
        for (const crossSectionData of crossSections) {
            crossSectionData.leftCrossSectionPolyline = [];
            crossSectionData.rightCrossSectionPolyline = [];
            for (const outline3d of crossSectionData.leftOutline.outline3D) {
                const polylineWGS84LeftPos = await site.convertPointsToWGS84(outline3d);
                const leftCrossSectionPolylineEntity = EarthBrainUtils.createLineEntity(viewer, "lineSection", polylineWGS84LeftPos, getDefaultLineWorkCrossSectionSettings());
                leftCrossSectionPolylineEntity.addProperty("pickInfo"); //Information when this polyline gets picked
                leftCrossSectionPolylineEntity["pickInfo"] = {
                    assetType: AssetType.ArbitraryLine_Data,
                    pickableType: CESIUM_PICKABLE_ENTITY.LINEWORKS_CROSS_SECTION_LEFT,
                    entity: leftCrossSectionPolylineEntity,
                    owner: this._currentLine,
                };
                crossSectionData.leftCrossSectionPolyline.push(leftCrossSectionPolylineEntity);
            }
            for (const outline3d of crossSectionData.rightOutline.outline3D) {
                const polylineWGS84RightPos = await site.convertPointsToWGS84(outline3d);
                const rightCrossSectionPolylineEntity = EarthBrainUtils.createLineEntity(viewer, "lineSection", polylineWGS84RightPos, getDefaultLineWorkCrossSectionSettings());
                rightCrossSectionPolylineEntity.addProperty("pickInfo"); //Information when this polyline gets picked
                rightCrossSectionPolylineEntity["pickInfo"] = {
                    assetType: AssetType.ArbitraryLine_Data,
                    pickableType: CESIUM_PICKABLE_ENTITY.LINEWORKS_CROSS_SECTION_RIGHT,
                    entity: rightCrossSectionPolylineEntity,
                    owner: this._currentLine,
                };
                crossSectionData.rightCrossSectionPolyline.push(rightCrossSectionPolylineEntity);
            }
        }
        return crossSections;
    }
    dispose(lineID) {
        this.deactivateLineWork(lineID);
        const crossSections = this._crossSectionManager.getCrossSectionsForLine(lineID);
        if (crossSections) {
            for (const crossSection of crossSections) {
                crossSection.leftCrossSectionPolyline.forEach((entity) => {
                    entity.show = false;
                    this._viewer.entities.remove(entity);
                });
                crossSection.rightCrossSectionPolyline.forEach((entity) => {
                    entity.show = false;
                    this._viewer.entities.remove(entity);
                });
                crossSection.leftOutline.outline3D.length = 0;
                crossSection.leftOutline.outline2D.clear();
                crossSection.rightOutline.outline3D.length = 0;
                crossSection.rightOutline.outline2D.clear();
                const labelEntity = crossSection.referencePoint?.wgs84data?.label;
                labelEntity && this._viewer.entities.remove(labelEntity);
                const sphereEntity = crossSection.referencePoint?.wgs84data?.sphere;
                if (sphereEntity?.pickInfo?.assetType ===
                    AssetType.ArbitraryLine_CROSS_SECTION) {
                    this._viewer.entities.remove(sphereEntity);
                }
            }
        }
        this._crossSectionManager.remove(lineID);
        this._activeCrossSectionData = undefined;
        this._originalSphere = undefined;
        this._originalPolylineMaterial = undefined;
    }
    /**
     * Finalize cross-section rendering by updating state and UI
     * This method handles all side effects after cross-section calculation
     */
    async finalizeCrossSectionRendering(lineId, crossSectionPoints) {
        const heightMaps = this._site.getActiveTraceableMaps();
        const crossSections = await this.addCrossSection(crossSectionPoints, heightMaps, this._site, this._viewer);
        this._crossSectionManager.addCrossSection(lineId, crossSections);
        this.renderCrossSectionData(crossSections[0]);
        const label = crossSections[0].referencePoint.label ?? "";
        this._onPointClickCallback(crossSections[0].referencePoint.lineID, crossSections[0].referencePoint.pointType, label);
    }
}
//# sourceMappingURL=linework-cross-section-handler.js.map